import { useEffect, useState } from 'react';
import {
  Plus, Save, Trash2, User, Crown, Wrench, Users as UsersIcon, Mail, Search,
  CalendarClock, Banknote,
} from 'lucide-react';
import supabase from '../lib/supabase';
import {
  PageHeader, Section, Badge, Button, EmptyState, Modal, Field, Input, Select, StatusLine,
} from '../components/ui';

type Role = 'owner' | 'manager' | 'schedule_manager' | 'finance_manager' | 'staff';

type Member = {
  id: string;
  club_id: string;
  user_id: string;
  role: Role;
  created_at: string;
  profiles?: { full_name: string | null };
  // We also look up the email via the resolve-by-email RPC for display.
  email?: string;
};

const ROLE_BADGES: Record<Role, { tone: 'lime' | 'blue' | 'amber' | 'gray'; icon: any; label: string }> = {
  owner:            { tone: 'lime',  icon: Crown,         label: 'Owner' },
  manager:          { tone: 'blue',  icon: Wrench,        label: 'Manager' },
  schedule_manager: { tone: 'blue',  icon: CalendarClock, label: 'Schedule mgr' },
  finance_manager:  { tone: 'amber', icon: Banknote,      label: 'Finance mgr' },
  staff:            { tone: 'gray',  icon: UsersIcon,     label: 'Staff' },
};

/**
 * Club owners land here to invite / remove teammates. Non-owners still
 * see the roster but don't get the management controls (guarded by
 * the DB trigger and RLS, we also just hide them).
 */
export default function ClubStaff({ clubId }: { clubId: string }) {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const load = async () => {
    setLoading(true);
    const [own, mems] = await Promise.all([
      supabase.rpc('owns_club', { p_club_id: clubId }),
      supabase
        .from('club_members')
        .select('id, club_id, user_id, role, created_at, profiles!user_id(full_name)')
        .eq('club_id', clubId)
        .order('created_at'),
    ]);
    setIsOwner(!!own.data);
    setMembers((mems.data ?? []) as unknown as Member[]);
    setLoading(false);
  };

  useEffect(() => { load(); }, [clubId]);

  const changeRole = async (m: Member, role: Role) => {
    setMsg(null);
    const { error } = await supabase.from('club_members').update({ role }).eq('id', m.id);
    if (error) setMsg({ kind: 'error', text: error.message });
    else { setMsg({ kind: 'success', text: `${m.profiles?.full_name ?? 'Teammate'} is now ${role}.` }); load(); }
  };

  const remove = async (m: Member) => {
    if (!confirm(`Remove ${m.profiles?.full_name ?? 'this teammate'} from the club?`)) return;
    setMsg(null);
    const { error } = await supabase.from('club_members').delete().eq('id', m.id);
    if (error) setMsg({ kind: 'error', text: error.message });
    else { setMsg({ kind: 'success', text: 'Removed.' }); load(); }
  };

  return (
    <div className="p-8 max-w-4xl">
      <PageHeader
        eyebrow="Club"
        title="Team"
        subtitle={isOwner
          ? 'Invite owners, managers, or staff to help run this club.'
          : 'Everyone who can manage this club. Ask an owner to change roles.'}
        actions={isOwner
          ? <Button icon={Plus} onClick={() => setShowAdd(true)}>Invite teammate</Button>
          : undefined}
      />

      {msg && <div className="mb-4"><StatusLine kind={msg.kind}>{msg.text}</StatusLine></div>}

      <Section className="p-0 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading team…</div>
        ) : members.length === 0 ? (
          <EmptyState
            icon={UsersIcon}
            title="No teammates yet"
            description={isOwner
              ? "You're the only one managing this club. Invite someone to help."
              : 'No other managers on file for this club.'}
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50/70 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Member</th>
                <th className="text-left px-5 py-3 font-semibold">Role</th>
                <th className="text-left px-5 py-3 font-semibold">Joined</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const RoleIcon = ROLE_BADGES[m.role].icon;
                return (
                  <tr key={m.id} className="border-t border-gray-100 hover:bg-gray-50/60">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                          <User className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 truncate">
                            {m.profiles?.full_name ?? '—'}
                          </div>
                          <div className="text-xs text-gray-500 font-mono">
                            {m.user_id.slice(0, 8)}…
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <Badge tone={ROLE_BADGES[m.role].tone} icon={RoleIcon}>
                        {ROLE_BADGES[m.role].label}
                      </Badge>
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-600">
                      {new Date(m.created_at).toLocaleDateString('en-GB', {
                        day: '2-digit', month: 'short', year: 'numeric',
                      })}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {isOwner && (
                        <div className="flex gap-2 justify-end">
                          <Select
                            value={m.role}
                            onChange={(e) => changeRole(m, e.target.value as Role)}
                            className="w-44 h-8 text-xs"
                          >
                            <option value="owner">Owner</option>
                            <option value="manager">Manager</option>
                            <option value="schedule_manager">Schedule manager</option>
                            <option value="finance_manager">Finance manager</option>
                            <option value="staff">Staff</option>
                          </Select>
                          <Button size="sm" variant="danger" icon={Trash2}
                            onClick={() => remove(m)}>Remove</Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Section>

      {showAdd && (
        <InviteForm
          clubId={clubId}
          onClose={() => setShowAdd(false)}
          onSaved={async () => { setShowAdd(false); await load(); }}
        />
      )}
    </div>
  );
}

function InviteForm({
  clubId, onClose, onSaved,
}: { clubId: string; onClose: () => void; onSaved: () => void }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('manager');
  const [lookup, setLookup] = useState<{ user_id: string; email: string; full_name: string | null } | null>(null);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const find = async () => {
    if (!email.trim()) return;
    setSearching(true); setErr(null);
    const { data, error } = await supabase.rpc('resolve_user_by_email', { p_email: email.trim() });
    setSearching(false);
    if (error) { setErr(error.message); setLookup(null); return; }
    const row = (data as any[])?.[0];
    if (!row) {
      setErr('No AceRent user with that email. Ask them to sign up on the customer app first.');
      setLookup(null);
    } else {
      setLookup(row);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lookup) { await find(); return; }
    setBusy(true); setErr(null);
    const { error } = await supabase.from('club_members').insert({
      club_id: clubId,
      user_id: lookup.user_id,
      role,
    });
    setBusy(false);
    if (error) {
      if (error.message.includes('duplicate key')) {
        setErr('This user is already a member of the club.');
      } else {
        setErr(error.message);
      }
      return;
    }
    onSaved();
  };

  return (
    <Modal
      title="Invite a teammate"
      subtitle="They need to already have an AceRent account. Search by email to find them."
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Mail className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                type="email" value={email}
                onChange={(e) => { setEmail(e.target.value); setLookup(null); }}
                placeholder="teammate@example.com"
                className="pl-9"
              />
            </div>
            <Button type="button" variant="secondary" icon={Search}
              loading={searching} onClick={find}>Find</Button>
          </div>
        </Field>

        {lookup && (
          <div className="rounded-lg bg-[#c6f806]/15 border border-[#2d5016]/20 p-3 text-sm">
            Found <strong>{lookup.full_name ?? lookup.email}</strong>
            {lookup.full_name && <span className="text-gray-500"> · {lookup.email}</span>}
          </div>
        )}

        <Field label="Role">
          <Select value={role} onChange={(e) => setRole(e.target.value as Role)}>
            <option value="owner">Owner — full control, including team management</option>
            <option value="manager">Manager — edit the club, courts, schedule, finance</option>
            <option value="schedule_manager">Schedule manager — hours, closures, court availability</option>
            <option value="finance_manager">Finance manager — payouts, refunds, pricing</option>
            <option value="staff">Staff — view-only on most pages</option>
          </Select>
        </Field>

        {err && <StatusLine kind="error">{err}</StatusLine>}

        <div className="flex gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} className="flex-1">Cancel</Button>
          <Button type="submit" loading={busy} icon={Save} className="flex-1" disabled={!lookup}>
            Add to club
          </Button>
        </div>
      </form>
    </Modal>
  );
}
